import { useCallback, useEffect, useRef, useState } from 'react';
import { type CameraFeature, cameraSourceLabel, cameraStreamTypeLabel } from '@tsr/shared-types';
import { CameraMedia } from './CameraMedia';

/**
 * The click-to-fly video overlay (Phase V, the "demo moment").
 *
 * Mounted once the Cesium camera has finished diving to the selected camera.
 * Renders the live player (or snapshot) centred over a semi-transparent black
 * backdrop at 70 % screen width, 16:9. The coverage cone stays visible around
 * the overlay, giving directional context. Dismissed by the "Back to map"
 * button, Esc, or clicking the backdrop — all call onClose, which flies the
 * Cesium camera back out.
 *
 * Keyboard (Phase V Part 3): Esc closes; F toggles native fullscreen on the
 * video element.
 */
export function CameraOverlay({
  camera,
  onClose,
  pinned,
  onTogglePin,
}: {
  camera: CameraFeature;
  onClose: () => void;
  /** #196 — whether this camera is pinned to the operations panel. */
  pinned?: boolean;
  /** #196 — pin/unpin this camera (origin of the pin action). */
  onTogglePin?: () => void;
}): JSX.Element {
  const playerWrapRef = useRef<HTMLDivElement | null>(null);
  // Drive the fade-in: mount at opacity-0, flip to opacity-100 just after mount
  // so the CSS transition runs. rAF gives the smooth one-frame delay; a short
  // timer is a fallback for when rAF is throttled (a backgrounded tab), so the
  // overlay can never get stuck invisible.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    const timer = setTimeout(() => setShown(true), 50);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
      return;
    }
    // Fullscreen the actual <video> when present (Part 3 wording), else the
    // player wrapper.
    const wrap = playerWrapRef.current;
    const target = wrap?.querySelector('video') ?? wrap;
    void target?.requestFullscreen?.();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, toggleFullscreen]);

  return (
    <div
      data-testid="camera-overlay"
      // Clicking the backdrop (but not the player) closes.
      onClick={onClose}
      className={`fixed inset-0 z-40 flex items-center justify-center bg-black/70 transition-opacity duration-300 ${
        shown ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        // Stop backdrop-close when interacting with the player itself.
        onClick={(e) => e.stopPropagation()}
        className="flex w-[70%] max-w-[1600px] flex-col gap-2"
      >
        <div className="flex items-center justify-between text-slate-100">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold" data-testid="overlay-title">
              {camera.properties.name}
            </h2>
            <p className="truncate text-xs text-slate-400">
              {cameraSourceLabel(camera.properties.source)} ·{' '}
              {cameraStreamTypeLabel(camera.properties.stream_type)}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {onTogglePin && (
              <button
                type="button"
                data-testid="overlay-pin"
                aria-pressed={pinned}
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePin();
                }}
                title={pinned ? 'Unpin from operations panel' : 'Pin to operations panel'}
                className={`rounded border px-3 py-1.5 text-xs ${
                  pinned
                    ? 'border-amber-500 bg-amber-700/70 text-amber-100 hover:bg-amber-700'
                    : 'border-slate-600 bg-slate-800/80 text-slate-100 hover:bg-slate-700'
                }`}
              >
                {pinned ? '📌 Pinned' : '📌 Pin'}
              </button>
            )}
            <button
              type="button"
              data-testid="overlay-back"
              onClick={onClose}
              className="rounded border border-slate-600 bg-slate-800/80 px-3 py-1.5 text-xs text-slate-100 hover:bg-slate-700"
            >
              ← Back to map
            </button>
          </div>
        </div>
        <div ref={playerWrapRef} data-testid="overlay-player">
          <CameraMedia camera={camera} />
        </div>
        <p className="text-center text-[11px] text-slate-400">
          Esc or click outside to return · F for fullscreen
        </p>
      </div>
    </div>
  );
}
